import { Button, styled } from "@mui/material";
import React from "react";
import StorageIcon from "@mui/icons-material/Storage";
import { useDispatchWrapper } from "../../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../../redux/messages/messagesActions";
import { extractHeadersFromFileV2 } from "../../market-campaign/helpers/extractHeadersFromFile";
import { IMarketCampaignAudience } from "../../../redux/marketCampaign/marketCampaign.types";

interface IExcelFileUplaodButtonAsyncV2Props {
  onUploadSuccess: (
    path: string,
    extractedHeaders: string[],
    data: Record<string, any>[],
    file?: File,
  ) => void;
}
export const ExcelFileUplaodButtonAsyncV2: React.FC<
  IExcelFileUplaodButtonAsyncV2Props
> = ({ onUploadSuccess }) => {
  const dispatch = useDispatchWrapper();

  const handleFileUploadAsync = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files || event.target.files?.length == 0) return;
    try {
      dispatch(saveLoaderProgress());
      const file = event.target.files[0] as File;

      // Extract headers using frontend parsing
      const headerResult = await extractHeadersFromFileV2(file);

      if (headerResult.success) {
        // Generate a mock path for local file (similar to server response format)
        const mockPath = `local_file_${Date.now()}_${file.name}`;

        // Call success callback with local file URL and extracted data
        onUploadSuccess(
          mockPath,
          headerResult.headers,
          headerResult.data,
          file,
        );

        dispatch(
          showMessage({
            type: "success",
            message:
              "File processed successfully! Headers extracted from local file.",
            displayAs: "snackbar",
          }),
        );
      } else {
        dispatch(
          showMessage({
            type: "error",
            message:
              headerResult.message || "Failed to extract headers from file",
            displayAs: "snackbar",
          }),
        );
      }
    } catch (error) {
      dispatch(
        showMessage({
          type: "error",
          message: "Failed to process file",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

  return (
    <>
      <Button
        component="label"
        variant="contained"
        color="primary"
        size="large"
        startIcon={<StorageIcon />}
      >
        Import Data From Excel/CSV
        <input
          type="file"
          hidden
          accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .csv"
          onChange={handleFileUploadAsync}
        />
      </Button>
    </>
  );
};

export const extractHeaders = <T extends Record<string, any>>(
  list: T[],
): string[] => {
  if (!Array.isArray(list) || list.length === 0) return [];
  return Object.keys(list[0]);
};

export const extractCombinedHeaders = (
  audienceLists: IMarketCampaignAudience[],
): string[] => {
  const headerSet = new Set<string>();

  audienceLists.forEach((list) => {
    (list.audience_list || []).forEach((row: Record<string, any>) => {
      Object.keys(row).forEach((key) => {
        if (key !== "check") {
          headerSet.add(key);
        }
      });
    });
  });

  return Array.from(headerSet);
};
